import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  CreditCard, 
  Search, 
  DollarSign,
  Calendar,
  User,
  Phone,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  Loader2,
  MessageCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { KvkkConsentModal } from '@/components/kvkk/KvkkConsentModal';
import { fetchCompanyForPDF } from '@/lib/pdf-generator';

type PaymentRow = Database['public']['Tables']['sale_payments']['Row'];
type SaleRow = Database['public']['Tables']['sales']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];
type PaymentType = Database['public']['Enums']['payment_type_enum'];

interface OpenPayment extends PaymentRow {
  sale: SaleRow;
  customer: CustomerRow;
}

interface PaymentRequest {
  amount: number;
  method: PaymentType;
  reference?: string;
  note?: string;
}

export default function Collections() {
  const { toast } = useToast();
  const { companyId, loading: authLoading } = useAuth();
  
  const [openPayments, setOpenPayments] = useState<OpenPayment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<OpenPayment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<OpenPayment | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>({
    amount: 0,
    method: 'cash',
    reference: '',
    note: ''
  });

  // SMS sending state
  const [sendingSMS, setSendingSMS] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [lastCollectedAmount, setLastCollectedAmount] = useState<number>(0);
  const [queuedSMSData, setQueuedSMSData] = useState<{
    phone: string;
    customerName: string;
    collectedAmount: number;
    collectionTime: string;
    remainingToday: number;
    totalBalance: number;
    companyName: string;
  } | null>(null);

  const fetchOpenPayments = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sale_payments')
        .select(`
          *,
          sale:sales(*),
          customer:sales(customers(*))
        `)
        .eq('company_id', companyId)
        .in('payment_status', ['open', 'partial'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Flatten the data structure
      const formattedData: OpenPayment[] = data.map(payment => ({
        ...payment,
        sale: payment.sale as SaleRow,
        customer: (payment.customer as any).customers as CustomerRow
      }));

      setOpenPayments(formattedData);
      setFilteredPayments(formattedData);
    } catch (error) {
      console.error('Error fetching open payments:', error);
      toast({
        title: "Hata",
        description: "Açık ödemeler yüklenirken hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchOpenPayments();
    }
  }, [companyId]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredPayments(openPayments);
      return;
    }

    const filtered = openPayments.filter(payment => {
      const customerName = `${payment.customer?.first_name || ''} ${payment.customer?.last_name || ''}`.trim();
      const customerPhone = payment.customer?.phone || '';
      const ref = payment.ref || '';
      
      return (
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customerPhone.includes(searchTerm) ||
        ref.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    setFilteredPayments(filtered);
  }, [searchTerm, openPayments]);

  const getCustomerName = (customer: CustomerRow) => {
    if (!customer) return 'Bilinmeyen Müşteri';
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Açık</Badge>;
      case 'partial':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Kısmi</Badge>;
      case 'completed':
        return <Badge variant="default"><CheckCircle className="w-3 h-3 mr-1" />Tamamlandı</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentTypeLabel = (type: PaymentType) => {
    const labels: Record<PaymentType, string> = {
      cash: 'Nakit',
      credit_card: 'Kredi Kartı',
      odero: 'Odero',
      paro: 'Paro',
      transfer: 'Transfer',
      promissory: 'Senet',
      other: 'Diğer'
    };
    return labels[type] || type;
  };

  // Format phone number for SMS (add +90 if not present)
  const formatPhoneForSMS = (phone: string): string => {
    if (!phone) return '';
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 90, add + prefix
    if (cleaned.startsWith('90')) {
      return '+' + cleaned;
    }
    
    // If it starts with 0, replace with +90
    if (cleaned.startsWith('0')) {
      return '+90' + cleaned.substring(1);
    }
    
    // If it doesn't start with country code, add +90
    if (cleaned.length === 10) {
      return '+90' + cleaned;
    }
    
    // If it already looks like it has country code but no +, add +
    if (cleaned.length > 10 && !phone.startsWith('+')) {
      return '+' + cleaned;
    }
    
    return phone;
  };

  // Format phone number for KVKK check (normalize to match database format: 90XXXXXXXXXX)
  const formatPhoneForKvkk = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // If it's already in database format (starts with 90 and has 12 digits), return as-is
    if (cleaned.startsWith('90') && cleaned.length === 12) {
      return cleaned;
    }
    
    // If it starts with 90 but has more digits, extract the 12-digit version
    if (cleaned.startsWith('90') && cleaned.length > 12) {
      return cleaned.substring(0, 12);
    }
    
    // If it's 10 digits (Turkish mobile format), prepend 90
    if (cleaned.length === 10) {
      return '90' + cleaned;
    }
    
    // If it starts with 0 followed by 10 digits, remove 0 and prepend 90
    if (cleaned.startsWith('0') && cleaned.length === 11) {
      return '90' + cleaned.substring(1);
    }
    
    // If it's 11 digits starting with 5 (mobile), prepend 90
    if (cleaned.length === 11 && cleaned.startsWith('5')) {
      return '90' + cleaned;
    }
    
    // If it's 9 digits (without leading 5), prepend 905
    if (cleaned.length === 9) {
      return '905' + cleaned;
    }
    
    // Return as-is if it doesn't match any pattern
    return cleaned;
  };

  // Check KVKK consent status for a phone number
  const checkKvkkConsent = async (phone: string): Promise<'granted' | 'no_record' | 'denied' | 'withdrawn' | 'unknown'> => {
    try {
      const formattedPhone = formatPhoneForKvkk(phone);
      if (!formattedPhone) {
        return 'unknown';
      }

      const { data, error } = await supabase.rpc('kvkk_get_status_for_phone', {
        p_phone: formattedPhone,
      });

      if (error) {
        console.error('Error checking KVKK consent:', error);
        return 'unknown';
      }

      if (!data || data.length === 0) {
        return 'no_record';
      }

      const consentStatus = data[0].consent_status as 'granted' | 'denied' | 'withdrawn';
      return consentStatus;
    } catch (err) {
      console.error('Exception checking KVKK consent:', err);
      return 'unknown';
    }
  };

  // Get collection data for SMS (collected amount, remaining today, total balance)
  const getCollectionData = async (customerId: string, collectedAmount: number, collectionTime: string) => {
    if (!companyId) return null;

    try {
      // First, get all sales for this customer
      const { data: customerSales, error: salesError } = await supabase
        .from('sales')
        .select('id')
        .eq('company_id', companyId)
        .eq('customer_id', customerId);

      if (salesError) throw salesError;

      const saleIds = (customerSales || []).map(s => s.id);
      if (saleIds.length === 0) {
        return {
          remainingToday: 0,
          totalBalance: 0,
        };
      }

      // Get all open payments for this customer today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      const { data: todayPayments, error: todayError } = await supabase
        .from('sale_payments')
        .select('*')
        .eq('company_id', companyId)
        .in('sale_id', saleIds)
        .in('payment_status', ['open', 'partial'])
        .gte('created_at', todayStart.toISOString())
        .lte('created_at', todayEnd.toISOString());

      if (todayError) throw todayError;

      // Calculate remaining today (excluding the one we just collected)
      const remainingToday = (todayPayments || []).reduce((sum, p) => {
        return sum + (p.remaining_amount || 0);
      }, 0);

      // Get total balance for this customer (all open/partial payments)
      const { data: allPayments, error: allError } = await supabase
        .from('sale_payments')
        .select('*')
        .eq('company_id', companyId)
        .in('sale_id', saleIds)
        .in('payment_status', ['open', 'partial']);

      if (allError) throw allError;

      const totalBalance = (allPayments || []).reduce((sum, p) => {
        return sum + (p.remaining_amount || 0);
      }, 0);

      return {
        remainingToday: Math.max(0, remainingToday), // Ensure non-negative
        totalBalance: Math.max(0, totalBalance), // Ensure non-negative
      };
    } catch (error) {
      console.error('Error getting collection data:', error);
      return {
        remainingToday: 0,
        totalBalance: 0,
      };
    }
  };

  // Send collection SMS message via Supabase Edge Function
  const sendCollectionSMS = async (
    customerPhone: string,
    customerName: string,
    collectedAmount: number,
    collectionTime: string,
    remainingToday: number,
    totalBalance: number,
    companyName: string
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      const smsPhone = formatPhoneForSMS(customerPhone);
      if (!smsPhone) {
        throw new Error('Geçerli telefon numarası bulunamadı');
      }

      const requestBody = {
        to: smsPhone,
        customer_name: customerName,
        company_name: companyName,
        collected_amount: collectedAmount,
        collection_time: collectionTime,
        remaining_today: remainingToday,
        total_balance: totalBalance,
      };

      console.log('📤 Sending collection SMS request:', requestBody);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-collection-sms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'SMS mesajı gönderilemedi');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Collection SMS message error:', error);
      throw error;
    }
  };

  // Execute SMS sending
  const executeSMS = async () => {
    if (!queuedSMSData) {
      return;
    }

    try {
      setSendingSMS(true);

      await sendCollectionSMS(
        queuedSMSData.phone,
        queuedSMSData.customerName,
        queuedSMSData.collectedAmount,
        queuedSMSData.collectionTime,
        queuedSMSData.remainingToday,
        queuedSMSData.totalBalance,
        queuedSMSData.companyName
      );

      toast({
        title: "SMS Mesajı Gönderildi! 📱",
        description: `Tahsilat bilgisi ${queuedSMSData.phone} numarasına SMS ile gönderildi`,
      });

      setShowPhoneModal(false);
      setShowKvkkModal(false);
      setQueuedSMSData(null);
      setSmsPhone('');
    } catch (smsError) {
      console.error('SMS sending failed:', smsError);
      toast({
        title: "SMS Gönderimi Başarısız",
        description: smsError instanceof Error ? smsError.message : "SMS mesajı gönderilemedi",
        variant: "destructive",
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleSendSMSFromModal = async () => {
    if (!smsPhone.trim()) {
      toast({
        title: "Hata",
        description: "Telefon numarası gerekli",
        variant: "destructive",
      });
      return;
    }

    if (!selectedPayment || !companyId) {
      toast({
        title: "Hata",
        description: "Tahsilat bilgileri eksik",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get company name
      const companyData = await fetchCompanyForPDF(companyId);
      
      // Get collection data
      const collectionData = await getCollectionData(
        selectedPayment.customer.id,
        lastCollectedAmount,
        new Date().toISOString()
      );

      if (!collectionData) {
        toast({
          title: "Hata",
          description: "Tahsilat verileri alınamadı",
          variant: "destructive",
        });
        return;
      }

      // Check KVKK consent before sending
      const consentStatus = await checkKvkkConsent(smsPhone);

      if (consentStatus === 'granted') {
        // Consent is granted, send SMS immediately
        setQueuedSMSData({
          phone: smsPhone,
          customerName: getCustomerName(selectedPayment.customer),
          collectedAmount: lastCollectedAmount,
          collectionTime: new Date().toISOString(),
          remainingToday: collectionData.remainingToday,
          totalBalance: collectionData.totalBalance,
          companyName: companyData.name,
        });
        await executeSMS();
      } else if (consentStatus === 'denied') {
        // Consent is denied, show error
        toast({
          title: "KVKK Onayı Reddedilmiş",
          description: "Müşteri KVKK onayını reddetmiş. SMS gönderilemez.",
          variant: "destructive",
        });
      } else {
        // No consent record or withdrawn - queue SMS and show KVKK modal
        setQueuedSMSData({
          phone: smsPhone,
          customerName: getCustomerName(selectedPayment.customer),
          collectedAmount: lastCollectedAmount,
          collectionTime: new Date().toISOString(),
          remainingToday: collectionData.remainingToday,
          totalBalance: collectionData.totalBalance,
          companyName: companyData.name,
        });
        setShowPhoneModal(false);
        setShowKvkkModal(true);
      }
    } catch (error) {
      console.error('Error preparing SMS:', error);
      toast({
        title: "Hata",
        description: "SMS hazırlanırken hata oluştu",
        variant: "destructive",
      });
    }
  };

  // Handle KVKK consent granted callback
  const handleKvkkConsentGranted = async () => {
    if (queuedSMSData) {
      await executeSMS();
    }
  };

  const handleCollectPayment = async () => {
    if (!selectedPayment || !companyId || paymentRequest.amount <= 0) {
      toast({
        title: "Geçersiz Miktar",
        description: "Ödeme miktarı sıfırdan büyük olmalıdır",
        variant: "destructive",
      });
      return;
    }

    if (paymentRequest.amount > (selectedPayment.remaining_amount || 0)) {
      toast({
        title: "Aşırı Ödeme",
        description: "Ödeme miktarı kalan borçtan fazla olamaz",
        variant: "destructive",
      });
      return;
    }

    try {
      const newRemainingAmount = (selectedPayment.remaining_amount || 0) - paymentRequest.amount;
      const newStatus = newRemainingAmount === 0 ? 'completed' : 'partial';

      // Create a new payment record for the partial payment
      const { error: paymentError } = await supabase
        .from('sale_payments')
        .insert({
          company_id: companyId,
          sale_id: selectedPayment.sale_id,
          amount: paymentRequest.amount,
          payment_type: paymentRequest.method,
          ref: paymentRequest.reference || null,
          paid_at: new Date().toISOString(),
          payment_status: 'completed',
          parent_payment_id: selectedPayment.id,
          original_amount: paymentRequest.amount,
          remaining_amount: 0
        });

      if (paymentError) throw paymentError;

      // Update the original payment record
      const { error: updateError } = await supabase
        .from('sale_payments')
        .update({
          remaining_amount: newRemainingAmount,
          payment_status: newStatus
        })
        .eq('id', selectedPayment.id);

      if (updateError) throw updateError;

      // Update sale total_paid and balance_due
      const newTotalPaid = (selectedPayment.sale.total_paid || 0) + paymentRequest.amount;
      const newBalanceDue = (selectedPayment.sale.balance_due || 0) - paymentRequest.amount;

      const { error: saleError } = await supabase
        .from('sales')
        .update({
          total_paid: newTotalPaid,
          balance_due: newBalanceDue
        })
        .eq('id', selectedPayment.sale_id);

      if (saleError) throw saleError;

      toast({
        title: "Tahsilat Başarılı",
        description: `₺${paymentRequest.amount.toLocaleString('tr-TR')} tahsil edildi`,
      });

      // Store collection info for SMS
      const collectedAmount = paymentRequest.amount;
      const collectionCustomer = selectedPayment.customer;

      // Reset form and close dialog
      setPaymentRequest({ amount: 0, method: 'cash', reference: '', note: '' });
      setShowPaymentDialog(false);
      
      // Refresh the list
      await fetchOpenPayments();

      // Show SMS modal after successful collection
      if (collectionCustomer?.phone) {
        setSmsPhone(collectionCustomer.phone);
        setLastCollectedAmount(collectedAmount);
        setSelectedPayment(selectedPayment); // Keep selected payment for SMS data
        setShowPhoneModal(true);
      } else {
        setSelectedPayment(null);
        setLastCollectedAmount(0);
      }

    } catch (error) {
      console.error('Error collecting payment:', error);
      toast({
        title: "Hata",
        description: "Tahsilat işlemi sırasında hata oluştu",
        variant: "destructive",
      });
    }
  };

  const openPaymentDialog = (payment: OpenPayment) => {
    setSelectedPayment(payment);
    setPaymentRequest({
      amount: payment.remaining_amount || 0,
      method: 'cash',
      reference: '',
      note: ''
    });
    setShowPaymentDialog(true);
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="ml-2">Tahsilatlar yükleniyor...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Tahsilatlar</h1>
            <p className="text-muted-foreground">Açık ödemeleri takip edin ve tahsilatları gerçekleştirin</p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Müşteri, telefon veya referans ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-80"
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-destructive/10 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Açık Ödemeler</p>
                  <p className="text-2xl font-bold">
                    {filteredPayments.filter(p => p.payment_status === 'open').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Kısmi Ödemeler</p>
                  <p className="text-2xl font-bold">
                    {filteredPayments.filter(p => p.payment_status === 'partial').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Toplam Alacak</p>
                  <p className="text-2xl font-bold">
                    ₺{filteredPayments.reduce((sum, p) => sum + (p.remaining_amount || 0), 0).toLocaleString('tr-TR')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payments List */}
        <Card>
          <CardHeader>
            <CardTitle>Açık Ödemeler</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium mb-2">Açık ödeme bulunamadı</h3>
                <p className="text-sm">
                  {searchTerm ? 'Arama kriterlerine uygun ödeme yok' : 'Tüm ödemeler tamamlanmış'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPayments.map((payment) => (
                  <div key={payment.id} className="p-4 border rounded-lg hover:bg-accent/5 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className="bg-accent/20 text-accent">
                            {getCustomerName(payment.customer).split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{getCustomerName(payment.customer)}</h4>
                            {getPaymentStatusBadge(payment.payment_status)}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {payment.customer?.phone || 'Telefon yok'}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(payment.created_at).toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Ödeme Türü: </span>
                              <span className="font-medium">{getPaymentTypeLabel(payment.payment_type)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Orijinal: </span>
                              <span className="font-medium">₺{(payment.original_amount || payment.amount).toLocaleString('tr-TR')}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Kalan: </span>
                              <span className="font-medium text-destructive">₺{(payment.remaining_amount || 0).toLocaleString('tr-TR')}</span>
                            </div>
                            {payment.ref && (
                              <div>
                                <span className="text-muted-foreground">Ref: </span>
                                <span className="font-medium">{payment.ref}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => openPaymentDialog(payment)}
                          disabled={!payment.remaining_amount || payment.remaining_amount <= 0}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Tahsil Et
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KVKK Consent Modal */}
        {showKvkkModal && queuedSMSData && companyId && (
          <KvkkConsentModal
            open={showKvkkModal}
            onOpenChange={setShowKvkkModal}
            phone={formatPhoneForKvkk(queuedSMSData.phone)}
            companyId={companyId}
            onConsentGranted={handleKvkkConsentGranted}
          />
        )}

        {/* SMS Phone Number Modal */}
        <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                📱 SMS Gönderimi
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sms-phone">Telefon Numarası</Label>
                <Input
                  id="sms-phone"
                  type="tel"
                  placeholder="+90 5XX XXX XX XX"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tahsilat bilgisi bu numaraya SMS ile gönderilecek
                </p>
              </div>

              {selectedPayment && (
                <div className="p-3 bg-accent/10 rounded-md">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{getCustomerName(selectedPayment.customer)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Seçili müşteri - farklı bir numaraya da gönderebilirsiniz
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button 
                  onClick={handleSendSMSFromModal}
                  disabled={sendingSMS || !smsPhone.trim()}
                  className="w-full"
                >
                  {sendingSMS ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      SMS Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      SMS Gönder
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPhoneModal(false);
                    setSelectedPayment(null);
                    setSmsPhone('');
                    setLastCollectedAmount(0);
                  }}
                  className="w-full"
                >
                  İptal Et
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Collection Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tahsilat Gerçekleştir</DialogTitle>
            </DialogHeader>
            {selectedPayment && (
              <div className="space-y-4">
                <div className="p-4 bg-accent/5 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-accent/20 text-accent text-xs">
                        {getCustomerName(selectedPayment.customer).split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{getCustomerName(selectedPayment.customer)}</p>
                      <p className="text-sm text-muted-foreground">{selectedPayment.customer?.phone}</p>
                    </div>
                  </div>
                  <Separator className="my-2" />
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Orijinal Borç:</span>
                      <span>₺{(selectedPayment.original_amount || selectedPayment.amount).toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between font-medium text-destructive">
                      <span>Kalan Borç:</span>
                      <span>₺{(selectedPayment.remaining_amount || 0).toLocaleString('tr-TR')}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="paymentAmount">Tahsil Edilecek Miktar *</Label>
                  <Input
                    id="paymentAmount"
                    type="number"
                    value={paymentRequest.amount || ''}
                    onChange={(e) => setPaymentRequest(prev => ({ 
                      ...prev, 
                      amount: parseFloat(e.target.value) || 0 
                    }))}
                    placeholder="0.00"
                    step="0.01"
                    max={selectedPayment.remaining_amount || 0}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Maksimum: ₺{(selectedPayment.remaining_amount || 0).toLocaleString('tr-TR')}
                  </p>
                </div>

                <div>
                  <Label>Ödeme Yöntemi</Label>
                  <Select 
                    value={paymentRequest.method} 
                    onValueChange={(value: PaymentType) => 
                      setPaymentRequest(prev => ({ ...prev, method: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Nakit</SelectItem>
                      <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                      <SelectItem value="odero">Odero</SelectItem>
                      <SelectItem value="paro">Paro</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                      <SelectItem value="promissory">Senet</SelectItem>
                      <SelectItem value="other">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="paymentReference">Referans</Label>
                  <Input
                    id="paymentReference"
                    value={paymentRequest.reference}
                    onChange={(e) => setPaymentRequest(prev => ({ 
                      ...prev, 
                      reference: e.target.value 
                    }))}
                    placeholder="İşlem ID, makbuz no vb."
                  />
                </div>

                <div>
                  <Label htmlFor="paymentNote">Not</Label>
                  <Input
                    id="paymentNote"
                    value={paymentRequest.note}
                    onChange={(e) => setPaymentRequest(prev => ({ 
                      ...prev, 
                      note: e.target.value 
                    }))}
                    placeholder="Tahsilat notları..."
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={() => setShowPaymentDialog(false)}
                  >
                    İptal
                  </Button>
                  <Button 
                    className="flex-1" 
                    onClick={handleCollectPayment}
                    disabled={!paymentRequest.amount || paymentRequest.amount <= 0}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Tahsil Et
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
